import {
  LeadTrackDTO,
  LeadTrackStageDTO,
  LeadTrackActivityItemDTO,
  DeveloperDTO,
  DeveloperUserDTO,
} from "../dtos";
import { DeveloperTrack, Stage, StageStatus } from "../types";

// ============================================================================
// Stage Transformers
// ============================================================================

/**
 * Determine stage status based on position relative to current stage
 * Uses status.currentStage.name to determine which stage is active
 */
const getStageStatusByPosition = (
  stageOrder: number,
  currentStageOrder: number | null
): StageStatus => {
  if (currentStageOrder === null) return "pending";
  
  if (stageOrder < currentStageOrder) return "completed";
  if (stageOrder === currentStageOrder) return "current";
  return "pending";
};

/**
 * Transform API stage DTOs to UI Stage type
 * Uses status.currentStage.name to determine which stage is active
 */
export const transformStages = (
  stages: LeadTrackStageDTO[],
  currentStageName: string | null
): Stage[] => {
  // Sort by order to ensure correct sequence
  const sortedStages = [...stages].sort((a, b) => a.order - b.order);
  
  // Find the order of the current stage
  const currentStage = sortedStages.find((s) => s.name === currentStageName);
  const currentStageOrder = currentStage?.order ?? null;
  
  return sortedStages.map((stage) => ({
    id: `stage-${stage.id}`,
    name: stage.name,
    status: getStageStatusByPosition(stage.order, currentStageOrder),
    order: stage.order,
  }));
};

// ============================================================================
// Track Transformers
// ============================================================================

/**
 * Transform API LeadTrackDTO to UI DeveloperTrack type
 */
export const transformLeadTrackToUI = (track: LeadTrackDTO): DeveloperTrack => {
  const developerUser = track.developerUser;
  // Check for developer info from multiple sources:
  // 1. Directly on track (track.developer)
  // 2. Nested in developerUser (track.developerUser.developer)
  const developer = track.developer || developerUser?.developer;
  const status = track.status;

  // Get current stage name from status
  const currentStageName = status?.currentStage?.name || null;

  return {
    id: track.id.toString(),
    trackUId: track.uid,
    developerName: developer?.name || "Unknown Developer",
    developerLogo: developer?.logoPath || undefined,
    developerUId: developer?.uid,
    agentName: developerUser?.name || "Unassigned",
    agentUId: developerUser?.uid,
    phoneNumber: developerUser?.phoneNumber || "",
    currentStage: currentStageName || "",
    currentStatus: status?.name || "Unknown",
    currentStatusColor: status?.color || "#F5F5F5",
    statusUId: status?.uid,
    stages: transformStages(track.stages, currentStageName),
    createdAt: track.createdAt,
    updatedAt: track.updatedAt,
  };
};

// ============================================================================
// Activity Transformers
// ============================================================================

export interface UIActivityItem {
  id: string;
  type: "comment" | "status_update" | "assignment_update";
  content: string;
  createdAt: string;
  actorName: string;
  actorColor: string;
  // For comments
  comment?: string;
  // For status updates
  previousStatus?: string;
  newStatus?: string;
  // For assignment updates
  previousAgent?: string;
  newAgent?: string;
}

/**
 * Transform API activity item to UI format
 */
export const transformActivityItem = (
  item: LeadTrackActivityItemDTO
): UIActivityItem => {
  return {
    id: item.uid,
    type: item.type,
    content: item.content,
    createdAt: item.createdAt,
    actorName: item.actorName,
    actorColor: item.actorColor,
    comment: item.comment || undefined,
    previousStatus:
      typeof item.previousStatus === "string"
        ? item.previousStatus
        : item.previousStatus?.name,
    newStatus:
      typeof item.newStatus === "string"
        ? item.newStatus
        : item.newStatus?.name,
    previousAgent: item.previousDeveloperUser?.name,
    newAgent: item.newDeveloperUser?.name,
  };
};

// ============================================================================
// Developer/User Option Transformers (for dropdowns)
// ============================================================================

export interface DeveloperOption {
  uid: string;
  name: string;
  logoPath: string | null;
}

export interface DeveloperUserOption {
  uid: string;
  name: string;
  phoneNumber: string;
  email: string;
}

/**
 * Transform API DeveloperDTO to dropdown option format
 */
export const transformDeveloperToOption = (
  developer: DeveloperDTO
): DeveloperOption => ({
  uid: developer.uid,
  name: developer.name,
  logoPath: developer.logoPath,
});

/**
 * Transform API DeveloperUserDTO to dropdown option format
 */
export const transformDeveloperUserToOption = (
  user: DeveloperUserDTO
): DeveloperUserOption => ({
  uid: user.uid,
  name: user.name,
  phoneNumber: user.phoneNumber,
  email: user.email,
});

